import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import apiService from '../../services/api';
import type { GoogleContinueSuccess } from './GoogleContinueButton';

type Props = {
  onSuccess: (result: GoogleContinueSuccess) => void;
};

type Status = { enabled?: boolean };

/** Continue with Microsoft on MailBox login. Popup + one-time handoff. */
export default function MicrosoftContinueButton({ onSuccess }: Props) {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiService
      .get<Status>('/api/Auth/microsoft/status')
      .then((data) => setEnabled(!!data?.enabled))
      .catch(() => setEnabled(false));
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setBusy(true);
    const apiOrigin = window.location.origin;
    const qs = new URLSearchParams({
      acceptTerms: 'true',
      loginContext: 'mailbox',
      checkoutContext: 'mailbox',
      openerOrigin: window.location.origin,
      returnUrl: window.location.href,
    });
    const popup = window.open(`/api/Auth/microsoft?${qs.toString()}`, 'qixit_microsoft_auth', 'width=520,height=640');
    if (!popup) {
      setBusy(false);
      setError('Popup blocked. Allow popups for this site.');
      return;
    }

    let settled = false;
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(closedPoll);
    };
    const onMessage = async (event: MessageEvent) => {
      const allowed =
        event.origin === apiOrigin ||
        event.origin === 'http://127.0.0.1:5220' ||
        event.origin === 'http://localhost:5220';
      if (!allowed) return;
      const data = event.data;
      if (!data || data.type !== 'socialAuth' || data.provider !== 'microsoft' || !data.handoffId) return;
      if (settled) return;
      settled = true;
      cleanup();
      try {
        const handoff = await apiService.get<{
          accessToken: string;
          refreshToken?: string;
          userId: string;
          username: string;
        }>(`/api/Auth/social-handoff/${encodeURIComponent(data.handoffId)}`);
        if (!handoff?.accessToken || !handoff.userId || !handoff.username) {
          throw new Error('Invalid handoff response');
        }
        onSuccess({
          accessToken: handoff.accessToken,
          refreshToken: handoff.refreshToken,
          userId: handoff.userId,
          username: handoff.username,
        });
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Microsoft sign-in failed');
      } finally {
        setBusy(false);
        try { popup.close(); } catch { /* ignore */ }
      }
    };
    window.addEventListener('message', onMessage);
    const closedPoll = window.setInterval(() => {
      if (popup.closed && !settled) {
        settled = true;
        cleanup();
        setBusy(false);
      }
    }, 500);
  }, [onSuccess]);

  if (!enabled) return null;

  return (
    <Box sx={{ width: '100%', mb: 1 }} data-testid="microsoft-continue">
      <Button
        variant="outlined"
        fullWidth
        disabled={busy}
        onClick={() => void start()}
        sx={{ textTransform: 'none', mb: 0.5 }}
        data-testid="microsoft-continue-button"
      >
        {busy ? 'Waiting for Microsoft…' : 'Continue with Microsoft'}
      </Button>
      {error && (
        <Typography color="error" variant="body2" data-testid="microsoft-continue-error">
          {error}
        </Typography>
      )}
    </Box>
  );
}
